/**
 * Block-artifact access + relevant-fragment extraction for the query canvas.
 *
 * The full transcript / scraped-HTML text for a block already ships to the
 * client on `GET /v1/block/:id/details` (as the `artifacts` array). This
 * module:
 *
 *   1. fetches + types those artifacts (`fetchBlockArtifacts`), and
 *   2. picks the passage most relevant to the user's question
 *      (`extractRelevantFragment`) — entirely client-side, so no LLM key
 *      ever touches the browser. It's a lightweight keyword-overlap scorer:
 *      good enough to surface the right couple of sentences next to a card,
 *      and swappable for a server-side LLM extraction later.
 *
 * For transcripts we keep segment timing so the fragment card can show a
 * "1:23" jump-in point; for HTML / scrapes we fall back to the artifact's
 * flat `searchableText`.
 */
import { getBlockDetails } from "@/api/query-api";
// ---------------------------------------------------------------------------
// Limits (see security-limits.mdc) — keep client-side scoring bounded.
// ---------------------------------------------------------------------------
/** Max chars of any artifact's `searchableText` we'll scan. */
const MAX_SCAN_CHARS = 200_000;
/** Max transcript segments we'll consider. */
const MAX_SEGMENTS = 5_000;
/** Target length of a returned fragment window. */
const TARGET_CHARS = 280;
/** Don't return a window shorter than this unless it's all there is. */
const MIN_CHARS = 140;
/** Artifact types whose `content.segments` carry transcript timing. */
const TRANSCRIPT_TYPES = new Set([
    "youtube_transcript",
    "audio_transcript",
    "instagram_reel_transcript",
]);
// ---------------------------------------------------------------------------
// Fetch
// ---------------------------------------------------------------------------
/**
 * Fetch a block's artifacts (full transcript / HTML / OCR text). Returns an
 * empty array on any failure — the canvas degrades to "no fragment" rather
 * than surfacing an error per card.
 */
export async function fetchBlockArtifacts(blockId) {
    try {
        const details = await getBlockDetails(blockId);
        // `/details` types artifacts loosely as unknown[]; the server validates
        // them against BlockArtifactSchema before sending, so a cast is safe here.
        return details.artifacts ?? [];
    }
    catch {
        return [];
    }
}
// A compact stoplist — enough to stop function words from dominating the
// overlap score without pulling in a full NLP dependency.
const STOPWORDS = new Set([
    "the", "and", "for", "are", "but", "not", "you", "all", "any", "can", "had",
    "her", "was", "one", "our", "out", "has", "him", "his", "how", "its", "may",
    "new", "now", "old", "see", "two", "way", "who", "did", "get", "use", "this",
    "that", "with", "from", "they", "have", "what", "your", "when", "will", "there",
    "their", "would", "about", "which", "them", "then", "than", "into", "some",
    "could", "been", "more", "were", "said", "like", "just", "also", "over", "such",
]);
function tokenize(text) {
    return text
        .toLowerCase()
        .replace(/[^a-z0-9\s]/g, " ")
        .split(/\s+/)
        .filter((t) => t.length >= 3 && !STOPWORDS.has(t));
}
/** Weighted query/answer terms — the question counts double the answer. */
function buildWeights(query, answer) {
    const weights = new Map();
    for (const t of tokenize(query))
        weights.set(t, (weights.get(t) ?? 0) + 2);
    for (const t of tokenize(answer))
        weights.set(t, (weights.get(t) ?? 0) + 1);
    return weights;
}
/**
 * Score a passage by the sum of weights of the DISTINCT query/answer terms it
 * contains. Counting each matched term once (not per occurrence) keeps long
 * passages from winning on length alone.
 */
function scorePassage(text, weights) {
    let score = 0;
    const seen = new Set();
    for (const t of tokenize(text)) {
        if (seen.has(t))
            continue;
        const w = weights.get(t);
        if (w !== undefined) {
            score += w;
            seen.add(t);
        }
    }
    return score;
}
/**
 * Slide a ~TARGET_CHARS window across consecutive units and return the
 * highest-scoring one. Each window starts at unit `i` and grows until it
 * reaches the target length, so windows align to natural unit boundaries
 * (sentences / transcript segments).
 */
function bestWindow(units, weights) {
    if (units.length === 0)
        return undefined;
    let best;
    for (let i = 0; i < units.length; i++) {
        let text = "";
        let j = i;
        while (j < units.length && text.length < TARGET_CHARS) {
            text = text ? `${text} ${units[j].text}` : units[j].text;
            j++;
        }
        if (text.length < MIN_CHARS && i > 0)
            break; // tail too short to bother
        const score = scorePassage(text, weights);
        if (!best || score > best.score) {
            best = { text: text.trim(), startSec: units[i].startSec, score };
        }
    }
    return best;
}
/** Collapse whitespace and trim a passage for display. */
function clean(text) {
    return text.replace(/\s+/g, " ").trim();
}
/** Split flat text into sentence-ish units for windowing. */
function toSentenceUnits(text) {
    const scoped = text.slice(0, MAX_SCAN_CHARS);
    return scoped
        .split(/(?<=[.!?])\s+|\n+/)
        .map((s) => clean(s))
        .filter((s) => s.length > 0)
        .map((s) => ({ text: s }));
}
/** Transcript segments → timed units (for the "jump to 1:23" affordance). */
function toSegmentUnits(artifact) {
    const content = artifact.content;
    const segments = content.segments ?? [];
    return segments.slice(0, MAX_SEGMENTS).map((seg) => ({
        text: clean(seg.text),
        startSec: seg.start,
    }));
}
/**
 * Pick the passage from a block's artifacts most relevant to the question.
 *
 * Transcript artifacts are preferred (their segment timing makes the richest
 * card); otherwise we scan the flat `searchableText` of the artifact with the
 * most text. Returns `undefined` only when there's no usable artifact text at
 * all — callers render an empty state in that case.
 */
export function extractRelevantFragment(artifacts, query, answer) {
    if (artifacts.length === 0)
        return undefined;
    const weights = buildWeights(query, answer);
    let best;
    // 1) Transcript artifacts — score over timed segments.
    for (const artifact of artifacts) {
        if (!TRANSCRIPT_TYPES.has(artifact.type))
            continue;
        const units = toSegmentUnits(artifact);
        const win = bestWindow(units, weights);
        if (win && (!best || win.score > best.score)) {
            best = { ...win, artifactType: artifact.type };
        }
    }
    // 2) Flat-text artifacts (HTML, OCR, scrapes) — score over sentences.
    for (const artifact of artifacts) {
        if (TRANSCRIPT_TYPES.has(artifact.type))
            continue;
        const text = artifact.searchableText;
        if (!text || text.trim().length === 0)
            continue;
        const win = bestWindow(toSentenceUnits(text), weights);
        if (win && (!best || win.score > best.score)) {
            best = { ...win, artifactType: artifact.type };
        }
    }
    if (!best)
        return undefined;
    return {
        text: clean(best.text),
        artifactType: best.artifactType,
        startSec: best.startSec,
    };
}
